import React, { useEffect, useState } from 'react';
import { getUserAnalyses, AnalysisRecord } from '@/lib/db/services/analysisService';
import { AnalysisHistoryItem } from './AnalysisHistoryItem';
import { Loader2, AlertCircle } from 'lucide-react';
import { DatabaseError } from '@/lib/utils/errors/types';

interface AnalysisHistoryProps {
  userId: string;
}

export function AnalysisHistory({ userId }: AnalysisHistoryProps) {
  const [analyses, setAnalyses] = useState<AnalysisRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function fetchAnalyses() {
      try {
        setLoading(true);
        setError(null);
        const data = await getUserAnalyses(userId);
        
        if (mounted) {
          setAnalyses(data);
        }
      } catch (err) {
        console.error('Error fetching analyses:', err);
        if (mounted) {
          const message = err instanceof DatabaseError 
            ? err.message 
            : 'Failed to load analysis history. Please try again.';
          setError(message);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchAnalyses();

    return () => {
      mounted = false;
    };
  }, [userId]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-red-500">
        <AlertCircle className="w-5 h-5" />
        <p>{error}</p>
      </div>
    );
  }

  if (analyses.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No analyses found. Start by analyzing your first website!
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Analysis History</h2>
      <div className="space-y-4">
        {analyses.map((analysis) => (
          <AnalysisHistoryItem key={analysis.id} analysis={analysis} />
        ))}
      </div>
    </div>
  );
}